import React, { useState, useEffect } from 'react';
import './StepSectionSelector.css';

const StepSectionSelector = ({ 
  sections, 
  contents, 
  onSelectionChange,
  selectedElements = {}
}) => {
  const [localSelections, setLocalSelections] = useState(selectedElements);
  const [inputValue, setInputValue] = useState('');
  const [productSelected, setProductSelected] = useState('');
  const [isOtherChoiceActive, setIsOtherChoiceActive] = useState({});

  // Organiser les contenus par section si c'est un tableau plat
  const contentsBySection = Array.isArray(contents) 
    ? contents.reduce((acc, content) => {
        const sectionId = content.section_id;
        if (!acc[sectionId]) {
          acc[sectionId] = [];
        }
        acc[sectionId].push(content);
        return acc;
      }, {})
    : contents;

  console.log('StepSectionSelector - Contents reçus:', contents);
  console.log('StepSectionSelector - Contents organisés par section:', contentsBySection);

  // Mettre à jour les sélections locales quand les props (venant de l'état global) changent.
  // C'est ce qui permet de conserver la surbrillance après un re-render du parent.
  useEffect(() => {
    if (selectedElements) {
      setLocalSelections(selectedElements);
    }
  }, [selectedElements]);

  // Gérer la sélection d'un produit
  const handleProductSelection = (contentId, productId, isSelected) => {
    if (!isSelected) return; // Ne rien faire si on désélectionne (radio)

    const newSelections = { ...localSelections };

    // Réinitialiser les sélections de produits pour tous les contenus de cette section
    // pour s'assurer qu'un seul radio est coché par section.
    const sectionId = contents.find(c => c.id === contentId)?.section_id;
    if (sectionId) {
      contents
        .filter(c => c.section_id === sectionId && c.content_type === 'PRODUCTS')
        .forEach(c => {
          if (newSelections[c.id]) {
            newSelections[c.id].selections = [];
          }
        });
      
      // Désactiver les "AUTRE CHOIX" de la même section et nettoyer leurs inputs
      const otherContents = contents.filter(c => c.section_id === sectionId && c.content_type === 'OTHER_CHOICES');
      if (otherContents.length > 0) {
        otherContents.forEach(oc => {
          if (newSelections[oc.id]) {
            newSelections[oc.id].inputs = {};
          }
        });
        // désactiver visuellement la carte "AUTRE CHOIX"
        setIsOtherChoiceActive(prev => {
          const next = { ...prev };
          otherContents.forEach(oc => { next[oc.id] = false; });
          return next;
        });
      }
    }

    // Définir la nouvelle sélection
    newSelections[contentId] = {
      ...(newSelections[contentId] || {}), // Garde les autres types de données (inputs, etc.)
      contentType: 'PRODUCTS',
      selections: [{ id: productId }],
    };
    
    setLocalSelections(newSelections);
    onSelectionChange && onSelectionChange(newSelections);
  };
  
  // Gérer la saisie d'un input
  const handleInputChange = (contentId, inputId, value) => {
    const newSelections = { ...localSelections };
    
    if (!newSelections[contentId]) {
      newSelections[contentId] = {
        contentType: 'INPUT',
        selections: [],
        inputs: {},
        otherChoices: []
      };
    }
    
    newSelections[contentId].inputs[inputId] = value;
    
    setLocalSelections(newSelections);
    onSelectionChange && onSelectionChange(newSelections);
  };

  // Gérer la selection d'un choice
  const handleChoiceSelection = (contentId, choiceId, isSelected) => {
    // Pour les boutons radio, on ne réagit qu'à la sélection.
    if (!isSelected) return;

    const newSelections = { ...localSelections };

    // Réinitialiser les autres choix de la même section pour simuler un groupe de radios
    const sectionId = contents.find(c => c.id === contentId)?.section_id;
    if (sectionId) {
      contents
        .filter(c => c.section_id === sectionId && c.content_type === 'CHOICES')
        .forEach(c => {
          if (newSelections[c.id]) {
            newSelections[c.id].choices = [];
          }
        });
    }

    // Définir la nouvelle sélection de choix
    newSelections[contentId] = {
      ...(newSelections[contentId] || {}),
      contentType: 'CHOICES',
      choices: [{ id: choiceId }],
    };

    setLocalSelections(newSelections);
    onSelectionChange && onSelectionChange(newSelections);
  };

  const handleToggleOtherChoice = (contentId) => {
    const willBeActive = !isOtherChoiceActive[contentId];

    // Mettre à jour l'état visuel d'abord
    setIsOtherChoiceActive(prev => ({ ...prev, [contentId]: willBeActive }));

    const content = contents.find(c => c.id === contentId);
    const sectionId = content?.section_id;
    const newSelections = { ...localSelections };

    if (willBeActive) {
      // Activation : désactiver toutes les sélections PRODUCTS et CHOICES de la même section
      if (sectionId) {
        contents
          .filter(c => c.section_id === sectionId)
          .forEach(c => {
            if (c.content_type === 'PRODUCTS' && newSelections[c.id]) {
              newSelections[c.id].selections = [];
            }
            if (c.content_type === 'CHOICES' && newSelections[c.id]) {
              newSelections[c.id].choices = [];
            }
          });
      }
      // S'assurer que l'entrée pour cet OTHER existe
      newSelections[contentId] = {
        ...(newSelections[contentId] || {}),
        contentType: 'OTHER_CHOICES',
        inputs: newSelections[contentId]?.inputs || {},
      };
    } else {
      // Désactivation : nettoyer les inputs associés à cet OTHER
      if (newSelections[contentId]) {
        newSelections[contentId].inputs = {};
      }
    }

    setLocalSelections(newSelections);
    onSelectionChange && onSelectionChange(newSelections);
  };

  // Gérer la saisie de texte
  const handleTextChange = (contentId, textValue) => {
    const newSelections = { ...localSelections };
    
    if (!newSelections[contentId]) {
      newSelections[contentId] = {
        contentType: 'TEXT',
        selections: [],
        inputs: {},
        otherChoices: [],
        textContent: ''
      };
    }
    
    newSelections[contentId].textContent = textValue;
    
    setLocalSelections(newSelections);
    onSelectionChange && onSelectionChange(newSelections);
  };

  // Vérifier si un produit est sélectionné
  const isProductSelected = (contentId, productId) => {
    return localSelections[contentId]?.selections?.some(p => p.id === productId) || false;
  };

  // Vérifier si un choix est sélectionné
  const isChoiceSelected = (contentId, choiceId) => {
    return localSelections[contentId]?.choices?.some(c => c.id === choiceId) || false;
  };

  // Obtenir la valeur d'un input
  const getInputValue = (contentId, inputId) => {
    return localSelections[contentId]?.inputs?.[inputId] || '';
  };

  return (
    <div className="step-section-selector">
      {sections.map((section) => (
        <div key={section.id} className="section mb-5">
          <div className="form-container">
            <div className="form-head">
              <p className="text-center">{section.title}</p>
            </div>
            <div className="be-form p-4 p-md-5">
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Regrouper tous les produits de la section dans une seule row */}
                <div className="row select-elements">
                  {contentsBySection[section.id]
                    ?.filter((content) => content.content_type === 'PRODUCTS')
                    .flatMap((content) =>
                      content.products?.map((product) => (
                        <div key={product.id} className="col-6 col-md-3 mb-4">
                          <label className="form-check-label materiau-label" htmlFor={`product-${content.id}-${product.id}`} style={{height: '100%', width: '100%', cursor: 'pointer'}}>
                            <input
                              type="radio"
                              id={`product-${content.id}-${product.id}`}
                              checked={isProductSelected(content.id, product.id)}
                              onChange={(e) => handleProductSelection(content.id, product.id, e.target.checked)}
                              name={`materiau-section-${section.id}`}
                              value={product.id}
                              className="materiau-radio"
                            />
                            <div className="materiau-wrapper">
                              <div className='materiau-image-container'>
                                <img
                                  src={product.image || '/assets/images/Solivage-plancher.png'}
                                  alt={product.name}
                                  style={{ maxWidth: '100%' }}
                                  className="materiau-image"
                                  loading="lazy"
                                />
                              </div>
                              
                              <div className="rectangle px-4 py-1">
                                <p className="Card-title text-ellipsis">{product.name}</p>
                                {product.description && (
                                  <p className="Card-sub-title text-ellipsis" style={{ opacity: 1, zIndex: 2 }}>
                                    {product.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))
                    )}
                </div>

                {/* Regrouper tous les choix de la section dans une seule row */}
                <div className="choices mt-4">
                  <div className="row">
                    {contentsBySection[section.id]
                      ?.filter((content) => content.content_type === 'CHOICES')
                      .flatMap((content) =>
                        content.choices?.map((choice) => ({ ...choice, contentId: content.id })) || []
                      )
                      .map((choice) => (
                        <div className="col-12 col-md-4" key={`${choice.contentId}-${choice.id}`}>
                          <label className="label-utility">
                            <input
                              id={`choice-${choice.contentId}-${choice.id}`}
                              checked={isChoiceSelected(choice.contentId, choice.id)}
                              onChange={(e) => handleChoiceSelection(choice.contentId, choice.id, e.target.checked)}
                              type="radio"
                              name={section.id} // Assurez-vous que les radios d'un même groupe ont le même `name`
                              value={choice.id}
                              className="utility-radio"
                            />
                            <div className="utility mb-3 px-2">
                              <span className='text-center text-ellipsi'>{choice.name}</span>
                            </div>
                          </label>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Afficher les autres types de contenu (non-produits) */}
                {contentsBySection[section.id]?.map((content) => (
                  <React.Fragment key={content.id}>
                    {/* Afficher les inputs avec le style du formulaire */}
                    {content.content_type === 'INPUT' && content.inputs && content.inputs.length > 0 && (
                      <div className="inputs">
                        {content.inputs.map((input) => (
                          <div className="mt-4" key={input.id}>
                            <div className="d-flex flex-column" style={{}}>
                              <label htmlFor="" className="label-be">
                                {input.label}
                              </label>
                              <div className="input-wrapper" style={{width: '400px'}}>
                                <input
                                  type={input.input_type === 'NUMBER' ? 'number' : input.input_type === 'TEXT' ? 'text' : 'radio'}
                                  className="form-control"
                                  placeholder={input.placeholder}
                                  value={getInputValue(content.id, input.id)}
                                  onChange={(e) => handleInputChange(content.id, input.id, e.target.value)}
                                />
                                <span className="unit">{input.suffix}</span>
                              </div>
                            </div>
                            <div className="to-consider">
                              {input.description && (
                                <p>Indicatons: {input.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Afficher les choix avec le style du formulaire */}
                    {/* Ce bloc est maintenant géré plus haut pour tout regrouper */}

                    {/* Afficher les autres choix avec le style du formulaire */}
                    {content.content_type === 'OTHER_CHOICES' &&
                      content.other_choices &&
                      content.other_choices.length > 0 && (
                        <div className="other-choice">
                          <div className="row">
                            <div className="col-3">
                              <div 
                                className={`other-choice-box ${isOtherChoiceActive[content.id] ? 'active' : ''}`}
                                onClick={() => handleToggleOtherChoice(content.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <p className="text-center">AUTRE CHOIX</p>
                              </div>
                            </div>
                            <div className="d-flex flex-column" style={{ width: 400 }}>                              
                              {content.other_choices.map(choice => (
                                choice.input && choice.input.map((i, index) => (
                                  <React.Fragment key={i.id || index}>
                                    <label htmlFor={`other-choice-input-${i.id}`} className="label-be">
                                      {i.label}
                                    </label>
                                    <div className="input-wrapper">
                                      <input 
                                        type={i.input_type.toLowerCase()} 
                                        id={`other-choice-input-${i.id}`} 
                                        placeholder={i.placeholder} 
                                        className="form-control mb-2" 
                                        value={getInputValue(content.id, i.id)}
                                        onChange={(e) => handleInputChange(content.id, i.id, e.target.value)}
                                        disabled={!isOtherChoiceActive[content.id]}
                                      />
                                      <span className="unit">{i.suffix}</span>
                                    </div>
                                    {i.description && <p className="to-consider mb-3">{i.description}</p>}
                                  </React.Fragment>
                                ))
                              ))}
                            </div>
                          </div>
                        </div>
                    )}

                    {/* Afficher le texte avec le style du formulaire */}
                    {content.content_type === 'TEXT' && (
                      // <div className="text-content">
                      <div className="">
                        <div className="be-form p-3 p-md-5">
                          <form action="">
                            <p className="be-description">
                              {content.texts.map((text, index) => (
                                <React.Fragment key={text.id}>
                                  <span className="">
                                    {text.text}
                                  </span>
                                  {index !== content.texts.length - 1 && (
                                    <span className="text-separator">
                                      <br />
                                    </span>
                                  )}
                                </React.Fragment>
                              ))}
                            </p>
                          </form>
                        </div>
                        {/* <div className="input-section mt-4">
                          <div className="d-flex flex-column">
                            <label htmlFor={`text-${content.id}`} className="input-label">
                              Votre texte :
                            </label>
                            <div className="input-wrapper">
                              <textarea
                                className="form-control"
                                id={`text-${content.id}`}
                                rows="4"
                                placeholder="Saisissez votre texte ici..."
                                value={localSelections[content.id]?.textContent || ''}
                                onChange={(e) => handleTextChange(content.id, e.target.value)}
                              />
                            </div>
                          </div>
                        </div> */}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </form>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StepSectionSelector;
